import React, { useEffect, useRef, useState } from "react"
import { Navigation } from "@kit/design/navigation"
import { Hint } from "@kit/design/hint"
import { CountBadge } from "@kit/design/count-badge"
import {
  NOTIFICATION_HINT_REQUESTED_EVENT,
  readPendingDrawerHint,
  clearPendingDrawerHint
} from "./notifications-events"

const HINT_ID = "bulk-notification"
const HINT_TIMEOUT_MS = 10000

// The footer Notifications item, plus the Hint that points at it after a bulk
// action leaves a result in the drawer. The action runs in a separate React
// root and signals via a window event carrying the hint copy; the listener runs
// unconditionally so an event fired before the item mounts still surfaces the
// hint once it's there to anchor to.
export function NotificationsNavItem({
  leading,
  unseenCount,
  hasTopics,
  onOpen
}) {
  const anchorRef = useRef(null)
  const [pendingHint, setPendingHint] = useState(null)

  useEffect(() => {
    const queueHint = event => setPendingHint(event.detail)
    window.addEventListener(NOTIFICATION_HINT_REQUESTED_EVENT, queueHint)
    return () =>
      window.removeEventListener(NOTIFICATION_HINT_REQUESTED_EVENT, queueHint)
  }, [])

  // A hint stashed by a flow that reloaded the page (e.g. an import) is picked
  // up here on mount; the show effect below waits for the item to exist to
  // anchor to, just as it does for a live event.
  useEffect(() => {
    const stashed = readPendingDrawerHint()
    if (stashed) setPendingHint(stashed)
  }, [])

  useEffect(() => {
    if (!pendingHint || !anchorRef.current) return
    Hint.show({
      id: HINT_ID,
      title: pendingHint.title,
      description: pendingHint.description,
      anchor: anchorRef.current,
      position: "right",
      timeout: HINT_TIMEOUT_MS
    })
    // Cleared once shown so a later mount can't resurface it.
    clearPendingDrawerHint()
    setPendingHint(null)
  }, [pendingHint, hasTopics])

  if (!hasTopics) return null

  const handleClick = () => {
    Hint.close(HINT_ID)
    onOpen()
  }

  return (
    <Navigation.Item
      leading={leading}
      label="Notifications"
      trailing={unseenCount > 0 ? <CountBadge count={unseenCount} /> : null}
      render={<button type="button" ref={anchorRef} onClick={handleClick} />}
    />
  )
}
